// ============================================================================
// @energyos/roof-core · grid.ts — planare Modulbelegung (deterministisch)
// ----------------------------------------------------------------------------
// Belegt ein RECHTECKIGES Dachfeld (Breite × Länge in Metern) mit einem
// regelmäßigen Modulraster. Anders als raster.ts (geo-basiert, Polygon vom
// Satelliten-Tracing) arbeitet diese Funktion rein PLANAR in lokalen Metern und
// liefert SVG-fähige Rechtecke (x/y/w/h) — der Pfad für die manuelle/numerische
// Dachflächen-Eingabe ohne Karte. Auto-Orientierung wählt die Belegung mit der
// höheren Modulzahl. Keine DB/API/LLM, deterministisch.
// ============================================================================
const EPS = 1e-9;
/** Wie viele Footprints der Breite b passen in length mit Spalt gap? */
function fit(lengthM, footprintM, gapM) {
    if (footprintM <= 0 || lengthM <= 0)
        return 0;
    // n*footprint + (n-1)*gap <= length  ⇔  n <= (length + gap) / (footprint + gap)
    return Math.max(0, Math.floor((lengthM + gapM + EPS) / (footprintM + gapM)));
}
function layoutFor(orientation, usableWidthM, usableLengthM, moduleWidthM, moduleHeightM, setbackM, moduleGapM, rowGapM) {
    // Hochformat: Kurzseite entlang Breite, Langseite entlang Länge.
    const footprintW = orientation === "portrait" ? moduleWidthM : moduleHeightM;
    const footprintH = orientation === "portrait" ? moduleHeightM : moduleWidthM;
    const cols = fit(usableWidthM, footprintW, moduleGapM);
    const rows = fit(usableLengthM, footprintH, rowGapM);
    const modules = [];
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            modules.push({
                xM: setbackM + c * (footprintW + moduleGapM),
                yM: setbackM + r * (footprintH + rowGapM),
                wM: footprintW,
                hM: footprintH,
                row: r,
                col: c,
            });
        }
    }
    return {
        orientation,
        cols,
        rows,
        moduleCount: cols * rows,
        usableWidthM,
        usableLengthM,
        modules,
    };
}
/**
 * Belegt ein rechteckiges Dachfeld planar mit Modulen.
 * Bei ungültigen/zu kleinen Maßen → leeres Ergebnis (0 Module), wirft nicht.
 */
export function gridLayoutPlanar(input) {
    const setbackM = input.setbackM ?? 0.3;
    const moduleGapM = input.moduleGapM ?? 0.02;
    const rowGapM = input.rowGapM ?? 0;
    const usableWidthM = Math.max(0, input.roofWidthM - 2 * setbackM);
    const usableLengthM = Math.max(0, input.roofLengthM - 2 * setbackM);
    if (!(input.moduleWidthM > 0) ||
        !(input.moduleHeightM > 0) ||
        usableWidthM <= 0 ||
        usableLengthM <= 0) {
        return {
            orientation: input.orientation === "landscape" ? "landscape" : "portrait",
            cols: 0,
            rows: 0,
            moduleCount: 0,
            usableWidthM,
            usableLengthM,
            modules: [],
        };
    }
    const mk = (o) => layoutFor(o, usableWidthM, usableLengthM, input.moduleWidthM, input.moduleHeightM, setbackM, moduleGapM, rowGapM);
    if (input.orientation === "portrait" || input.orientation === "landscape") {
        return mk(input.orientation);
    }
    // auto: ergiebigere Orientierung wählen (bei Gleichstand Hochformat).
    const p = mk("portrait");
    const l = mk("landscape");
    return l.moduleCount > p.moduleCount ? l : p;
}
/**
 * Leitet aus einer reinen Flächenangabe (m²) plausible Rechteck-Maße ab, wenn
 * keine echten Maße/kein Polygon vorliegen. aspect = Breite/Länge (Traufe meist
 * länger als Falllinie → > 1). Transparente Annahme, vom Benutzer überschreibbar.
 */
export function rectFromArea(areaM2, aspect = 1.6) {
    if (!(areaM2 > 0))
        return { roofWidthM: 0, roofLengthM: 0 };
    const a = aspect > 0 ? aspect : 1.6;
    const roofLengthM = Math.sqrt(areaM2 / a);
    const roofWidthM = a * roofLengthM;
    return { roofWidthM, roofLengthM };
}
//# sourceMappingURL=grid.js.map